import { type DatabaseAdapter } from '@databases/selections-api-types';
import { type DatabaseEntry, type DatabaseField } from '@databases/types/database';
import { type TableV2Projection } from '@databases/view-layout-table-v2/data';

interface TableV2SelectionDatabaseAdapterOptions {
  readonly projection: TableV2Projection | undefined;
  readonly getEntry: (entryId: string) => DatabaseEntry | undefined;
  readonly getSpaceKey: () => string;
}

/**
 * Uses the same projection order as the read-only Table V2 render model.
 * Entry entities stay lazy so initializing selections does not normalize every
 * value in a large database.
 */
export class TableV2SelectionDatabaseAdapter implements DatabaseAdapter {
  readonly #getEntry: TableV2SelectionDatabaseAdapterOptions['getEntry'];
  readonly #getSpaceKey: () => string;
  #fields: readonly DatabaseField[] | undefined;
  #fieldsById = new Map<string, DatabaseField>();
  readonly #options: TableV2SelectionDatabaseAdapterOptions;

  constructor(options: TableV2SelectionDatabaseAdapterOptions) {
    this.#options = options;
    this.#getEntry = options.getEntry;
    this.#getSpaceKey = options.getSpaceKey;
  }

  getVisibleFieldIds(): string[] {
    return [...(this.#options.projection?.visibleFieldIds ?? [])];
  }

  getVisibleEntryIds(): string[] {
    return [...(this.#options.projection?.entryIds ?? [])];
  }

  getVisibleEntryCount(): number {
    return (this.#options.projection?.entryIds ?? []).length;
  }

  getVisibleEntryIdAt(index: number): string | undefined {
    return index < 0 ? undefined : (this.#options.projection?.entryIds ?? [])[index];
  }

  getVisibleFieldIdAt(index: number): string | undefined {
    return index < 0
      ? undefined
      : (this.#options.projection?.visibleFieldIds ?? [])[index];
  }

  getFieldById(fieldId: string): DatabaseField | undefined {
    const fields = this.#options.projection?.fields;

    if (fields !== this.#fields) {
      this.#fields = fields;
      this.#fieldsById = new Map(fields?.map((field) => [field.id, field]));
    }

    return this.#fieldsById.get(fieldId);
  }

  getEntryById(entryId: string): DatabaseEntry | undefined {
    return this.#getEntry(entryId);
  }

  getSpaceKey(): string {
    return this.#getSpaceKey();
  }

  getAllFieldIds(): string[] {
    return this.#options.projection?.fields.map(({ id }) => id) ?? [];
  }

  canEditValue(_fieldId: string, _entryId: string): boolean {
    return false;
  }

  canRequestValueEdit(_fieldId: string, _entryId: string): boolean {
    return false;
  }

  canBulkEditValue(_fieldId: string, _entryIds: string[]): boolean {
    return false;
  }
}
